/**
 * ProgressBar Component
 *
 * A reusable, accessible progress bar that follows the project's dark-theme
 * design language (bg-black/* backgrounds, white text).
 *
 * Features:
 * - Dark theme by default (bg-black/30 track, purple gradient fill)
 * - Optional percentage label
 * - Smooth CSS transition animation
 * - Clamps percent to [0, 100] internally so callers don't need to guard
 * - Configurable fill color via the `color` prop (Tailwind class string)
 * - Configurable track height via the `height` prop (Tailwind class string)
 *
 * @example
 * ```tsx
 * // Basic usage
 * <ProgressBar percent={uploadProgress} />
 *
 * // With label and custom color
 * <ProgressBar percent={75} showLabel color="bg-green-500" />
 *
 * // Custom height
 * <ProgressBar percent={50} height="h-1" />
 * ```
 */

import { memo } from 'react';

export interface ProgressBarProps {
  /** Progress value from 0 to 100 (clamped automatically) */
  percent: number;
  /**
   * Tailwind class(es) for the fill color.
   * Defaults to a purple-to-pink gradient matching the app's primary accent.
   */
  color?: string;
  /**
   * Tailwind height class for the track.
   * Defaults to "h-2" (8px).
   */
  height?: string;
  /**
   * When true, a "<percent>%" label is rendered to the right of the bar.
   * Defaults to false.
   */
  showLabel?: boolean;
  /** Additional Tailwind classes applied to the outer wrapper div */
  className?: string;
}

/**
 * Reusable progress bar with dark theme and smooth animation.
 */
export const ProgressBar = memo(function ProgressBar({
  percent,
  color = 'bg-gradient-to-r from-purple-600 to-pink-600',
  height = 'h-2',
  showLabel = false,
  className = '',
}: ProgressBarProps) {
  const clamped = Math.min(100, Math.max(0, percent));

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Track */}
      <div
        className={`flex-1 ${height} bg-black/30 rounded-full overflow-hidden`}
        role="progressbar"
        aria-valuenow={clamped}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        {/* Fill */}
        <div
          className={`${height} ${color} rounded-full transition-all duration-300 ease-out`}
          style={{ width: `${clamped}%` }}
        />
      </div>

      {/* Optional label */}
      {showLabel && (
        <span className="text-xs font-medium text-white/70 tabular-nums w-9 text-right shrink-0">
          {Math.round(clamped)}%
        </span>
      )}
    </div>
  );
});
